import { apiClient, get, post, patch, getCookie } from './apiClient';

// Flag to prevent refresh token loops
let isRefreshing = false;
let refreshFailed = false;

// Token expiration management
let tokenExpiresAt: number | null = null;

// Function to set token expiration time based on server response
function updateTokenExpiration(expiresAt: string | undefined) {
  if (expiresAt) {
    tokenExpiresAt = new Date(expiresAt).getTime();
    console.log('Token expires at:', new Date(tokenExpiresAt).toLocaleString());
  } else {
    // If no expiration provided, default to 30 minutes
    tokenExpiresAt = Date.now() + 30 * 60 * 1000;
  }
}

// Function to check if token is about to expire (within 5 minutes)
function isTokenExpiringSoon(): boolean {
  if (!tokenExpiresAt) return false;
  const fiveMinutes = 5 * 60 * 1000;
  return Date.now() + fiveMinutes >= tokenExpiresAt;
}

// Interface for token response
interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_at: string;
}

// Function to refresh the token
async function refreshTokenOrRedirect(): Promise<void> {
  try {
    // Reset flag when we try a new refresh
    refreshFailed = false;
    
    // Skip interceptors for this call to avoid loops
    // Call with empty body as per backend update
    const response = await post<TokenResponse>(
      '/auth/refresh', 
      undefined,
      { skipAuth: true }
    );
    
    // Update expiration time based on response
    if (response.expires_at) {
      updateTokenExpiration(response.expires_at);
    }
    
    return Promise.resolve();
  } catch (error) {
    refreshFailed = true;
    // Clear token expiration time on failure
    tokenExpiresAt = null;
    return Promise.reject(error);
  }
}

// User interface
export interface User {
  id: string;
  username: string;
  email: string;
  is_active: boolean;
  organization_id: string;
  created_at: string;
}

// Function to check if user is authenticated by making an API call
export const isLoggedIn = async (): Promise<boolean> => {
  // If refresh already failed, don't try again
  if (refreshFailed) {
    return false;
  }
  
  try {
    // Only refresh the token if it's expiring soon and not already refreshing
    // This prevents excessive refresh calls
    if (isTokenExpiringSoon() && !isRefreshing) {
      isRefreshing = true;
      try {
        await refreshTokenOrRedirect();
        console.log('Token refreshed successfully');
      } catch (err) {
        console.error('Token refresh failed:', err);
        // Don't immediately fail - try the /user/me call anyway
      } finally {
        isRefreshing = false;
      }
    }
    
    // Use the /user/me endpoint to verify authentication
    const response = await get<User>('/user/me', { skipAuth: true });
    return !!response;
  } catch (error) {
    console.log('Authentication verification failed', error);
    
    // Only try a refresh on 401 errors if we haven't refreshed yet
    if (!isRefreshing && (error as any)?.status === 401) {
      isRefreshing = true;
      try {
        await refreshTokenOrRedirect();
        // If refresh succeeds, try the API call again
        const retryResponse = await get<User>('/user/me', { skipAuth: true });
        return !!retryResponse;
      } catch (refreshErr) {
        console.error('Final refresh attempt failed:', refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    
    return false;
  }
};

// Auth functions
export const authApi = {
  login: async (username: string, password: string): Promise<TokenResponse> => {
    // Reset refresh flags on new login
    refreshFailed = false;
    isRefreshing = false;
    
    // The backend expects form data for login, not JSON
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    formData.append('grant_type', 'password');
    
    try {
      // Use the raw fetch API for this specific endpoint since it needs form data
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:10000'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Login failed');
      }
      
      const data = await response.json() as TokenResponse;
      
      // Store token expiration time for proactive refreshing
      if (data.expires_at) {
        updateTokenExpiration(data.expires_at);
      }
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  logout: async () => {
    try {
      const response = await post('/auth/logout', undefined);
      return response;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },
  
  getUser: async (userId: string): Promise<User> => {
    try {
      const response = await get<User>(`/user/${userId}`);
      return response;
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  },
  
  getCurrentUser: async (): Promise<User> => {
    try {
      // Get current user using the /user/me endpoint
      const response = await get<User>('/user/me');
      
      if (response) {
        return response;
      }
      
      throw new Error('No user data available');
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },
  
  // Method to check if user is authenticated by making an API call
  isLoggedIn: isLoggedIn,
  
  // Helper method to clear auth state and cookies
  clearAuthState: () => {
    // Reset refresh flags
    refreshFailed = true;
    isRefreshing = false;
    
    // Clear token expiration
    tokenExpiresAt = null;
    
    // Clear cookies
    document.cookie = 'access_token=; Max-Age=0; path=/; domain=' + window.location.hostname;
    document.cookie = 'refresh_token=; Max-Age=0; path=/; domain=' + window.location.hostname;
    document.cookie = 'csrf_token=; Max-Age=0; path=/; domain=' + window.location.hostname;
  },
  
  // Change password
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      await patch('/user/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }
};